import React, { useState } from 'react';
import { cn } from '@fshr/shared-react';
import Button from '../button';
import { useTranslations } from '@fshr/context/i18n-context';
import { useTealium } from '@fshr/context/tealium-context';
import { formatPrice } from '@fshr/shared';

export type ActivityType = 'appointment' | 'activities' | 'dining' | 'standard' | 'amenity';

export interface ActivityCardProps {
	date: string;
	time?: string;
	vendor?: string;
	status: string;
	title?: string;
	type: ActivityType;
	totalCost?: string;
	guests?: string;
	quantity?: string;
	duration?: string;
	description?: string;
	notes?: string;
	currencyCode?: string;
	confirmationNumber?: string;
	departureLocation?: string;
	cancellationPolicy?: string;
	preferredTimeOfDay?: string;
	chatEnabled?: boolean;
}

export const ActivityCard: React.FC<ActivityCardProps> = ({
	date,
	time,
	vendor,
	status,
	title,
	type,
	totalCost,
	guests,
	quantity,
	duration,
	description,
	notes,
	currencyCode,
	confirmationNumber,
	departureLocation,
	cancellationPolicy,
	preferredTimeOfDay,
	chatEnabled,
}) => {
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const t = useTranslations();
	const { trackLink } = useTealium();

	const getTypeTitle = (type: ActivityType): string => {
		switch (type) {
			case 'appointment':
				return t('appointment');
			case 'activities':
				return t('Activity');
			case 'dining':
				return t('Dining Reservation');
			default:
				return t('Activity');
		}
	};

	const getDisplayStatus = (status: string): string | null => {
		const pendingStatuses = ['Open', 'Pending', 'Waitlist', 'On Hold', 'Requested'];
		const confirmedStatuses = ['Closed', 'Reconfirmed'];

		const normalizedStatus = status.toLowerCase();

		if (pendingStatuses.some(s => s.toLowerCase() === normalizedStatus)) {
			return 'Pending';
		}
		if (confirmedStatuses.some(s => s.toLowerCase() === normalizedStatus)) {
			return 'Confirmed';
		}
		return null;
	};

	const chatToUpdate = () => {
		trackLink('interaction', {
			interaction_type: 'itinerary overlay',
			interaction_name: 'chat to update',
			interaction_detail: 'itinerary timeline',
		});
		window.dispatchEvent(new Event('webchat.open'));
	};

	return (
		<div
			className="flex items-stretch spacing-inline-xs relative cursor-pointer"
			role="listitem"
			aria-label={`${t('Activity Details')} - ${date}`}
		>
			<div className="flex flex-col h-full">
				{/* Timeline dot and line */}
				<div
					className="absolute left-[3px] top-0 bottom-0 flex flex-col items-center"
					aria-hidden={true}
				>
					{/* Dot */}
					<div className="relative flex h-2 w-2">
						<div className="absolute h-2 w-2 rounded-full bg-[rgb(77,77,77,0.45)]" />
					</div>

					{/* Line */}
					<div className="w-[1px] h-full bg-[rgb(77,77,77,0.45)]" />
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 pl-100 pb-200">
				{/* Date */}
				<div className="text-label text-text-secondary mb-080">
					{date} {time && `- ${time}`}
				</div>

				{/* Card */}
				<div
					className="rounded bg-surface-raised-subtle p-200 border border-border-subtle cursor-pointer"
					aria-expanded={isDetailsOpen}
				>
					{/* Title and dropdown icon */}
					<div className="flex justify-between flex-col gap-080">
						<div
							className="flex justify-between items-center"
							onClick={() => setIsDetailsOpen(!isDetailsOpen)}
						>
							<h3 className={`text-subtitle2 text-text-primary${title ? '' : ' capitalize'}`}>
								{title || getTypeTitle(type)}
							</h3>
							<button
								className="w-6 h-6 flex items-center justify-center"
								aria-label={t(isDetailsOpen ? 'Collapse details' : 'Expand details')}
							>
								<div
									aria-hidden={true}
									className={cn([
										'pointer-events-none',
										"content=['']",
										'border-[6px]',
										'border-l-transparent',
										'border-r-transparent',
										'border-b-0',
										'border-t-black',
										'top-1/2',
										'-t-y-1/2',
										'ltr:right-[16px]',
										'rtl:left-0',
										isDetailsOpen ? 'rotate-180' : '',
									])}
								/>
							</button>
						</div>

						{/* Vendor */}
						<div className="text-caption">{vendor}</div>

						{/* Status */}
						{getDisplayStatus(status) && (
							<div
								className={`text-label uppercase ${isDetailsOpen ? 'pb-150' : ''} ${
									getDisplayStatus(status) === 'Pending' ? 'text-[#C57907]' : 'text-[#218242]'
								}`}
								data-status={status.toLowerCase()}
								role="status"
							>
								{t(getDisplayStatus(status)!)}
							</div>
						)}
					</div>

					{isDetailsOpen && (
						<div role="region" aria-label={t('Activity Details')}>
							{/* Details Grid */}
							<div className="grid grid-cols-2 gap-150 py-150 border-t border-border-subtle">
								<div>
									<h4 className="text-label text-text-primary">{t('Selected date')}</h4>
									<p className="text-body3 text-text-primary">{date}</p>
								</div>
								{(time || preferredTimeOfDay) && (
									<div>
										<h5 className="text-label text-text-primary">{t('Time')}</h5>
										<p className="text-body3 text-text-primary">{time || preferredTimeOfDay}</p>
									</div>
								)}
								{confirmationNumber && (
									<div>
										<h5 className="text-label text-text-primary">{t('Confirmation number')}</h5>
										<p className="text-body3 text-text-primary">{confirmationNumber}</p>
									</div>
								)}
								{totalCost && (
									<div>
										<h5 className="text-label text-text-primary">{t('Total cost')}</h5>
										<p className="text-body3 text-text-primary">
											{formatPrice({ value: totalCost, currencyCode })}
										</p>
									</div>
								)}
								{duration && (
									<div>
										<h5 className="text-label text-text-primary">{t('Duration')}</h5>
										<p className="text-body3 text-text-primary">{duration}</p>
									</div>
								)}
								{guests && (
									<div>
										<h5 className="text-label text-text-primary">{t('Guests')}</h5>
										<p className="text-body3 text-text-primary">{t(guests)}</p>
									</div>
								)}
								{departureLocation && (
									<div>
										<h5 className="text-label text-text-primary">{t('Departure location')}</h5>
										<p className="text-body3 text-text-primary">{departureLocation}</p>
									</div>
								)}
								{quantity && type === 'amenity' && (
									<div>
										<h5 className="text-label text-text-primary">{t('Quantity')}</h5>
										<p className="text-body3 text-text-primary">{quantity}</p>
									</div>
								)}
							</div>

							{/* Notes */}
							{notes && (
								<div className="mt-150">
									<h5 className="text-label text-text-primary">{t('Notes')}</h5>
									<div
										className="text-body3 text-text-primary"
										dangerouslySetInnerHTML={{ __html: notes }}
										role="note"
									/>
								</div>
							)}
							{/* Description */}
							{description && description !== 'null' && (
								<div className="mt-150">
									<h5 className="text-label text-text-primary">{t('Description')}</h5>
									<div
										className="text-body3 text-text-primary"
										dangerouslySetInnerHTML={{ __html: description || '' }}
										role="note"
									/>
								</div>
							)}
							{/* Cancellation Policy */}
							{cancellationPolicy && (
								<div className="mt-150">
									<h5 className="text-label text-text-primary">{t('Cancellation policy')}</h5>
									<div
										className="text-body3 text-text-primary"
										dangerouslySetInnerHTML={{ __html: t(cancellationPolicy) }}
										role="note"
									/>
								</div>
							)}

							{/* Chat Button */}
							{chatEnabled && (
								<Button
									intent="outline"
									size="small"
									className="mt-150"
									aria-label={t('Chat to update')}
									onClick={chatToUpdate}
								>
									{t('Chat to update')}
								</Button>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
